import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  LayoutDashboard, Package, ShoppingCart, RefreshCw, Plus, Minus, Trash2,
  DollarSign, AlertTriangle, Boxes, Store, Pencil, X, Check, Mail, Send, Tag, Settings,
  ChevronLeft, ChevronRight, Search, CalendarDays, Image as ImageIcon, Upload, Layers, Bell, Star, Sparkles,
  ArrowRight, ArrowUpRight, ExternalLink, TrendingUp, PackageX, Clock, Receipt,
} from "lucide-react";
import api, { formatCAD, formatApiErrorDetail, resolveImg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DeliveryZoneMap } from "@/components/DeliveryZoneMap";
import { StoreStatusBadge } from "@/components/StoreStatusBadge";
import { ASSETS } from "@/lib/assets";
import { categoryIcon } from "@/lib/categoryIcons";

const GMAP_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const field = "w-full rounded-lg border border-border bg-card px-3 py-2 text-sm outline-none focus:border-clay";
const STATUSES = ["pending", "processing", "shipped", "delivered", "cancelled"];
const PICKUP_STATUSES = ["pending", "processing", "ready", "cancelled"];
const DELIVERY_STATUSES = ["pending", "processing", "out_for_delivery", "delivered", "cancelled"];
const STATUS_LABELS = { ready: "Ready for pickup", out_for_delivery: "On the way" };
const ALL_STATUSES = ["pending", "processing", "ready", "out_for_delivery", "delivered", "cancelled"];
const statusLabel = (s) => STATUS_LABELS[s] || s;
const STATUS_COLORS = {
  pending: "bg-muted text-forest", processing: "bg-ochre/20 text-forest",
  ready: "bg-clay/15 text-clay", out_for_delivery: "bg-ochre/25 text-forest",
  shipped: "bg-clay/15 text-clay", delivered: "bg-forest/10 text-forest",
  cancelled: "bg-destructive/10 text-destructive",
};
const statusesFor = (o) => {
  const id = o.shipping_method?.id;
  if (id === "pickup") return PICKUP_STATUSES;
  if (id === "standard" || id === "express") return DELIVERY_STATUSES;
  return STATUSES;
};

const TONES = {
  green: "bg-leaf/15 text-forest",
  red: "bg-clay/10 text-clay",
  gold: "bg-ochre/20 text-[#8a5a00]",
  ink: "bg-ink/5 text-ink",
};

function StatCard({ icon: Icon, label, value, hint, tone = "green", onClick }) {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag
      onClick={onClick}
      className="group relative flex w-full flex-col overflow-hidden rounded-[1.5rem] border border-border/70 bg-white p-5 text-left transition-all hover:-translate-y-0.5 hover:shadow-[0_20px_40px_-24px_rgba(15,74,35,0.35)]"
    >
      <div className="flex items-center justify-between">
        <span className={`flex h-10 w-10 items-center justify-center rounded-xl ${TONES[tone]}`}>
          <Icon className="h-5 w-5" />
        </span>
        {onClick && <ArrowUpRight className="h-4 w-4 text-muted-foreground transition-colors group-hover:text-forest" />}
      </div>
      <p className="mt-5 font-display text-3xl font-semibold tracking-tight text-forest">{value}</p>
      <p className="mt-0.5 text-sm font-medium text-ink/70">{label}</p>
      {hint && <p className="mt-3 border-t border-border/70 pt-3 text-xs text-muted-foreground">{hint}</p>}
    </Tag>
  );
}

// Revenue per day for the last `days` days, from the orders list (Calgary-agnostic, local dates).
function dailyRevenue(orders, days = 14) {
  const buckets = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    buckets.push({ date: d, cents: 0, count: 0 });
  }
  const start = buckets[0].date.getTime();
  orders.forEach((o) => {
    if (!o.created_at || o.status === "cancelled") return;
    const t = new Date(o.created_at);
    t.setHours(0, 0, 0, 0);
    const idx = Math.round((t.getTime() - start) / 86400000);
    if (idx >= 0 && idx < days) {
      buckets[idx].cents += o.total_cents || 0;
      buckets[idx].count += 1;
    }
  });
  return buckets;
}

function RevenueChart({ data }) {
  const W = 640, H = 200, P = 8;
  const max = Math.max(...data.map((d) => d.cents), 1);
  const step = (W - P * 2) / (data.length - 1);
  const pts = data.map((d, i) => [P + i * step, H - P - (d.cents / max) * (H - P * 2 - 16)]);
  const line = pts.map((p, i) => `${i ? "L" : "M"}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(" ");
  const area = `${line} L${pts[pts.length - 1][0]},${H} L${pts[0][0]},${H} Z`;
  const empty = data.every((d) => d.cents === 0);
  return (
    <div className="relative">
      <svg viewBox={`0 0 ${W} ${H}`} className="h-48 w-full" preserveAspectRatio="none" role="img" aria-label="Revenue over the last 14 days">
        <defs>
          <linearGradient id="rev-fill" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#43A32F" stopOpacity="0.28" />
            <stop offset="100%" stopColor="#43A32F" stopOpacity="0" />
          </linearGradient>
        </defs>
        {[0.25, 0.5, 0.75].map((f) => (
          <line key={f} x1="0" x2={W} y1={H * f} y2={H * f} stroke="#E8E1D3" strokeDasharray="4 6" />
        ))}
        <path d={area} fill="url(#rev-fill)" />
        <path d={line} fill="none" stroke="#0F4A23" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
      </svg>
      <div className="mt-2 flex justify-between text-[11px] text-muted-foreground">
        {data.filter((_, i) => i % 3 === 0 || i === data.length - 1).map((d) => (
          <span key={d.date.toISOString()}>{d.date.toLocaleDateString("en-CA", { month: "short", day: "numeric" })}</span>
        ))}
      </div>
      {empty && (
        <div className="absolute inset-0 flex items-center justify-center pb-6">
          <p className="rounded-full bg-white/90 px-4 py-2 text-xs font-medium text-muted-foreground shadow-sm">No sales in the last 14 days — new orders will chart here.</p>
        </div>
      )}
    </div>
  );
}

const STATUS_BAR = {
  pending: "bg-ink/30", processing: "bg-ochre", ready: "bg-leaf", out_for_delivery: "bg-ochre",
  shipped: "bg-clay", delivered: "bg-forest", cancelled: "bg-destructive/70",
};

function Overview({ stats, square, onSync, syncing, orders = [], products = [], waitlist = [], carts = [], goTo }) {
  if (!stats) {
    return (
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-40 animate-pulse rounded-[1.5rem] bg-muted" />)}
      </div>
    );
  }
  const series = dailyRevenue(orders);
  const last14 = series.reduce((a, d) => a + d.cents, 0);
  const avgOrder = stats.order_count ? Math.round(stats.revenue_cents / stats.order_count) : 0;
  const todayCount = series[series.length - 1]?.count || 0;
  const outOfStock = products.filter((p) => (p.stock ?? 0) <= 0).length;
  const lowList = products
    .filter((p) => (p.stock ?? 0) > 0 && (p.stock ?? 0) <= 10)
    .sort((x, y) => x.stock - y.stock)
    .slice(0, 6);
  const recent = orders.slice(0, 6);
  const statusEntries = Object.entries(stats.status_counts || {}).sort((a, b) => b[1] - a[1]);
  const statusTotal = statusEntries.reduce((a, [, n]) => a + n, 0);
  const openCarts = carts.filter((c) => c.status === "active").length;
  const waiting = waitlist.reduce((a, r) => a + (r.waiting || 0), 0);

  return (
    <div className="space-y-5">
      {/* KPIs */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard icon={DollarSign} tone="green" label="Total revenue" value={formatCAD(stats.revenue_cents)} hint={`Avg order ${formatCAD(avgOrder)}`} />
        <StatCard icon={ShoppingCart} tone="red" label="Orders" value={stats.order_count} hint={`${todayCount} today · ${stats.status_counts?.pending || 0} pending`} onClick={() => goTo("orders")} />
        <StatCard icon={Boxes} tone="ink" label="Products" value={stats.product_count} hint={`${outOfStock} out of stock`} onClick={() => goTo("products")} />
        <StatCard icon={AlertTriangle} tone="gold" label="Low stock (≤10)" value={stats.low_stock_count} hint="Reorder in Square to restock" onClick={() => goTo("products")} />
      </div>

      {/* Chart + status */}
      <div className="grid gap-5 lg:grid-cols-3">
        <div className="rounded-[1.5rem] border border-border/70 bg-white p-6 lg:col-span-2">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="label-eyebrow text-muted-foreground">Revenue · last 14 days</p>
              <p className="mt-2 font-display text-3xl font-semibold text-forest">{formatCAD(last14)}</p>
            </div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-leaf/10 px-3 py-1 text-xs font-semibold text-forest">
              <TrendingUp className="h-3.5 w-3.5" /> {series.reduce((a, d) => a + d.count, 0)} orders
            </span>
          </div>
          <div className="mt-4"><RevenueChart data={series} /></div>
        </div>

        <div className="rounded-[1.5rem] border border-border/70 bg-white p-6">
          <p className="label-eyebrow text-muted-foreground">Orders by status</p>
          {statusTotal === 0 ? (
            <div className="flex h-[calc(100%-1.5rem)] flex-col items-center justify-center py-10 text-center">
              <Receipt className="h-9 w-9 text-muted-foreground/60" />
              <p className="mt-3 text-sm font-medium text-forest">No orders yet</p>
              <p className="mt-1 text-xs text-muted-foreground">Status breakdown appears after the first sale.</p>
            </div>
          ) : (
            <>
              <div className="mt-5 flex h-3 overflow-hidden rounded-full bg-muted">
                {statusEntries.map(([s, n]) => (
                  <span key={s} className={STATUS_BAR[s] || "bg-ink/30"} style={{ width: `${(n / statusTotal) * 100}%` }} />
                ))}
              </div>
              <ul className="mt-5 space-y-3">
                {statusEntries.map(([s, n]) => (
                  <li key={s} className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2 capitalize text-ink/80">
                      <span className={`h-2.5 w-2.5 rounded-full ${STATUS_BAR[s] || "bg-ink/30"}`} /> {statusLabel(s).replace(/_/g, " ")}
                    </span>
                    <span className="font-semibold text-forest">{n}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </div>

      {/* Recent orders + low stock */}
      <div className="grid gap-5 lg:grid-cols-2">
        <div className="rounded-[1.5rem] border border-border/70 bg-white p-6">
          <div className="flex items-center justify-between">
            <p className="font-display text-xl font-semibold text-forest">Recent orders</p>
            <button onClick={() => goTo("orders")} className="inline-flex items-center gap-1 text-xs font-bold text-clay hover:underline">
              View all <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </div>
          {recent.length === 0 ? (
            <div className="mt-6 rounded-2xl border border-dashed border-border py-10 text-center">
              <ShoppingCart className="mx-auto h-8 w-8 text-muted-foreground/60" />
              <p className="mt-3 text-sm text-muted-foreground">Orders placed online will show up here.</p>
            </div>
          ) : (
            <ul className="mt-4 divide-y divide-border/70">
              {recent.map((o) => (
                <li key={o.id} className="flex items-center gap-3 py-3">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-sand text-xs font-bold text-forest">
                    {(o.customer_name || "?").charAt(0).toUpperCase()}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-ink">{o.customer_name || "Guest"}</p>
                    <p className="text-xs text-muted-foreground">
                      {o.order_number} · {o.created_at ? new Date(o.created_at).toLocaleDateString("en-CA", { month: "short", day: "numeric" }) : ""}
                    </p>
                  </div>
                  <span className={`hidden rounded-full px-2.5 py-1 text-[11px] font-semibold capitalize sm:inline ${STATUS_COLORS[o.status] || "bg-muted text-forest"}`}>
                    {statusLabel(o.status).replace(/_/g, " ")}
                  </span>
                  <span className="w-20 text-right text-sm font-bold text-forest">{formatCAD(o.total_cents)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="rounded-[1.5rem] border border-border/70 bg-white p-6">
          <div className="flex items-center justify-between">
            <p className="font-display text-xl font-semibold text-forest">Running low</p>
            <button onClick={() => goTo("products")} className="inline-flex items-center gap-1 text-xs font-bold text-clay hover:underline">
              All products <ArrowRight className="h-3.5 w-3.5" />
            </button>
          </div>
          {lowList.length === 0 ? (
            <div className="mt-6 rounded-2xl border border-dashed border-border py-10 text-center">
              <Check className="mx-auto h-8 w-8 text-leaf" />
              <p className="mt-3 text-sm text-muted-foreground">Everything is well stocked.</p>
            </div>
          ) : (
            <ul className="mt-4 divide-y divide-border/70">
              {lowList.map((p) => (
                <li key={p.id} className="flex items-center gap-3 py-3">
                  <img src={resolveImg(p.image_url)} alt="" className="h-10 w-10 shrink-0 rounded-xl bg-sand object-cover" />
                  <p className="min-w-0 flex-1 truncate text-sm font-medium text-ink">{p.name}</p>
                  <span className={`rounded-full px-2.5 py-1 text-[11px] font-bold ${p.stock <= 3 ? "bg-clay/10 text-clay" : "bg-ochre/20 text-[#8a5a00]"}`}>
                    {p.stock} left
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Square + quick actions */}
      <div className="grid gap-5 lg:grid-cols-3">
        <div className="relative overflow-hidden rounded-[1.5rem] bg-forest p-6 text-sand lg:col-span-2">
          <div className="pattern-dots absolute inset-0" />
          <div className="relative flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/10 text-ochre">
                <Store className="h-6 w-6" />
              </div>
              <div>
                <p className="font-display text-xl font-semibold text-white">Square inventory sync</p>
                <p className="mt-0.5 flex items-center gap-2 text-sm text-sand/75">
                  <span className={`h-2 w-2 rounded-full ${square?.configured ? "animate-pulse bg-leaf" : "bg-ochre"}`} />
                  {square?.configured
                    ? `Connected · ${square.env} environment`
                    : "Not connected — add your Square Access Token & Location ID to enable live sync"}
                </p>
              </div>
            </div>
            <button
              data-testid="square-sync-btn"
              onClick={onSync}
              disabled={syncing}
              className="inline-flex items-center gap-2 rounded-full bg-ochre px-5 py-2.5 text-sm font-bold text-ink transition-colors hover:bg-white disabled:opacity-60"
            >
              <RefreshCw className={`h-4 w-4 ${syncing ? "animate-spin" : ""}`} /> Sync from Square
            </button>
          </div>
          <p className="relative mt-5 rounded-2xl bg-white/[0.07] p-4 text-xs leading-relaxed text-sand/75">
            Your catalog is managed in Square and syncs to the storefront automatically (and via “Sync from Square” on demand). Stock updates live as customers buy and through Square inventory webhooks.
          </p>
        </div>

        <div className="rounded-[1.5rem] border border-border/70 bg-white p-6">
          <p className="label-eyebrow text-muted-foreground">Needs attention</p>
          <div className="mt-4 space-y-2">
            {[
              { id: "waitlist", icon: Bell, label: "Back-in-stock requests", value: waiting },
              { id: "carts", icon: Mail, label: "Open abandoned carts", value: openCarts },
              { id: "products", icon: PackageX, label: "Out of stock", value: outOfStock },
              { id: "orders", icon: Clock, label: "Pending orders", value: stats.status_counts?.pending || 0 },
            ].map((a) => (
              <button
                key={a.id + a.label}
                onClick={() => goTo(a.id)}
                className="flex w-full items-center gap-3 rounded-2xl px-3 py-2.5 text-left transition-colors hover:bg-sand"
              >
                <a.icon className="h-4 w-4 text-forest" />
                <span className="flex-1 text-sm text-ink/80">{a.label}</span>
                <span className={`min-w-7 rounded-full px-2 py-0.5 text-center text-xs font-bold ${a.value ? "bg-clay text-white" : "bg-muted text-muted-foreground"}`}>{a.value}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function ImageDialog({ open, title, subtitle, current, onClose, onSubmit, onGenerate, withDescription = false, currentDescription = "" }) {
  const [url, setUrl] = useState(current || "");
  const [desc, setDesc] = useState(currentDescription || "");
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);

  useEffect(() => { if (open) { setUrl(current || ""); setDesc(currentDescription || ""); } }, [open, current, currentDescription]);
  if (!open) return null;

  const generate = async () => {
    setGenerating(true);
    try {
      const genUrl = await onGenerate(desc);
      setUrl(genUrl);
      toast.success("Image generated with AI");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Image generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUrl(data.url);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      await onSubmit(url.trim(), desc.trim());
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-forest/40 p-4" onClick={onClose}>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-2xl bg-card p-6 shadow-xl" data-testid="image-dialog">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-display text-2xl font-medium text-forest">{title}</h3>
            {subtitle && <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>}
          </div>
          <button onClick={onClose}><X className="h-5 w-5 text-muted-foreground" /></button>
        </div>

        <div className="mt-5 flex justify-center">
          <div className="h-32 w-32 overflow-hidden rounded-xl border border-border bg-sand">
            {url ? (
              <img src={resolveImg(url)} alt="preview" data-testid="image-dialog-preview" className="h-full w-full object-cover" />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-xs text-muted-foreground">No image</div>
            )}
          </div>
        </div>

        <label className="mt-5 flex cursor-pointer items-center justify-center gap-2 rounded-xl border border-dashed border-border bg-sand/40 py-3 text-sm font-medium text-forest hover:border-clay">
          <Upload className={`h-4 w-4 ${uploading ? "animate-pulse" : ""}`} />
          {uploading ? "Uploading…" : "Upload an image"}
          <input data-testid="image-dialog-file" type="file" accept="image/*" className="hidden" onChange={onFile} disabled={uploading} />
        </label>

        {onGenerate && (
          <button
            type="button"
            data-testid="image-dialog-generate"
            onClick={generate}
            disabled={generating}
            className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl bg-forest py-3 text-sm font-semibold text-sand transition-colors hover:bg-[#0A3318] disabled:opacity-60"
          >
            <Sparkles className={`h-4 w-4 ${generating ? "animate-pulse" : ""}`} />
            {generating ? "Generating with AI…" : "Generate with AI"}
          </button>
        )}

        <div className="my-3 flex items-center gap-3 text-xs uppercase tracking-wide text-muted-foreground">
          <span className="h-px flex-1 bg-border" /> or paste a URL <span className="h-px flex-1 bg-border" />
        </div>
        <input data-testid="image-dialog-url" className={field} placeholder="https://…" value={url} onChange={(e) => setUrl(e.target.value)} />

        {withDescription && (
          <div className="mt-4">
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">Short intro (shown on the category page)</label>
            <textarea
              data-testid="category-description"
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
              rows={3}
              placeholder="A friendly sentence or two about this category…"
              className={`${field} resize-none`}
            />
          </div>
        )}

        <div className="mt-5 flex gap-2">
          {current && (
            <button data-testid="image-dialog-remove" onClick={() => setUrl("")} className="rounded-full border border-border px-4 py-2.5 text-sm font-medium text-destructive hover:bg-destructive/5">
              Remove
            </button>
          )}
          <button data-testid="image-dialog-save" onClick={save} disabled={busy || uploading}
            className="flex-1 rounded-full bg-clay py-2.5 text-sm font-semibold text-white hover:bg-[#A3161F] disabled:opacity-60">
            {busy ? "Saving…" : "Save image"}
          </button>
        </div>
      </motion.div>
    </div>
  );
}

function WaitlistTab({ waitlist }) {
  const totalWaiting = waitlist.reduce((sum, r) => sum + r.waiting, 0);
  return (
    <div>
      <div className="mb-4 flex items-start gap-2 rounded-xl border border-border bg-sand/40 px-4 py-3">
        <Bell className="mt-0.5 h-4 w-4 shrink-0 text-clay" />
        <p className="text-xs text-muted-foreground">
          Shoppers who tapped <strong className="text-forest">"Notify me"</strong> on a product. Reorder the most-wanted items first — everyone waiting is emailed automatically once Square shows stock again.
        </p>
      </div>
      {waitlist.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-card p-10 text-center" data-testid="waitlist-empty">
          <Bell className="mx-auto h-10 w-10 text-muted-foreground" />
          <p className="mt-4 font-medium text-forest">No back-in-stock requests yet</p>
          <p className="mt-1 text-sm text-muted-foreground">When a shopper asks to be notified about a sold-out item, it shows up here.</p>
        </div>
      ) : (
        <>
          <p className="mb-3 text-sm text-muted-foreground" data-testid="waitlist-summary">
            <strong className="text-forest">{totalWaiting}</strong> shopper{totalWaiting !== 1 ? "s" : ""} waiting across <strong className="text-forest">{waitlist.length}</strong> product{waitlist.length !== 1 ? "s" : ""}
          </p>
          <div className="overflow-hidden rounded-2xl border border-border bg-card">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-border bg-sand/40 text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="p-4">Product</th>
                  <th className="p-4">Stock</th>
                  <th className="p-4 text-right">Waiting</th>
                </tr>
              </thead>
              <tbody>
                {waitlist.map((r) => (
                  <tr key={r.product_id} data-testid={`waitlist-row-${r.product_id}`} className="border-b border-border last:border-0">
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <img src={resolveImg(r.image_url) || undefined} alt="" className="h-11 w-11 rounded-lg object-cover" />
                        <div>
                          <p className="font-medium text-forest">{r.name}</p>
                          <p className="text-xs text-muted-foreground">{r.category} · {formatCAD(r.price_cents)}</p>
                        </div>
                      </div>
                    </td>
                    <td className="p-4">
                      {r.stock <= 0 ? (
                        <span className="rounded-full bg-destructive/10 px-2.5 py-0.5 text-xs font-semibold text-destructive">Sold out</span>
                      ) : (
                        <span className="rounded-full bg-forest/10 px-2.5 py-0.5 text-xs font-semibold text-forest">{r.stock} in stock</span>
                      )}
                    </td>
                    <td className="p-4 text-right">
                      <span data-testid={`waitlist-count-${r.product_id}`} className="inline-flex items-center gap-1.5 rounded-full bg-clay/10 px-3 py-1 text-sm font-bold text-clay">
                        <Bell className="h-3.5 w-3.5" /> {r.waiting}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function CategoriesTab({ categories, reload }) {
  const [editing, setEditing] = useState(null);
  const save = async (image_url, description) => {
    await api.put(`/admin/categories/${editing.slug}`, { image_url, description });
    toast.success("Category updated");
    reload();
  };
  const togglePin = async (c) => {
    try {
      await api.put(`/admin/categories/${c.slug}/featured`, { featured: !c.featured });
      toast.success(!c.featured ? `${c.name} pinned to the homepage` : `${c.name} unpinned`);
      reload();
    } catch (e) {
      toast.error("Could not update");
    }
  };
  const genImage = async () => {
    const { data } = await api.post(`/admin/categories/${editing.slug}/generate-image`);
    reload();
    return data.image;
  };
  const removeCategory = async (c) => {
    if (!window.confirm(`Delete the "${c.name}" category? This can't be undone.`)) return;
    try {
      await api.delete(`/admin/categories/${c.slug}`);
      toast.success("Category deleted");
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not delete");
    }
  };
  return (
    <div>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="font-display text-2xl font-semibold text-forest">{categories.length} categories</p>
          <p className="mt-1 text-sm text-muted-foreground">
            {categories.filter((c) => c.featured).length} pinned to the homepage · {categories.filter((c) => c.from_square).length} from Square
          </p>
        </div>
        <div className="flex max-w-xl items-start gap-2 rounded-2xl border border-border/70 bg-white px-4 py-3">
          <Store className="mt-0.5 h-4 w-4 shrink-0 text-leaf" />
          <p className="text-xs leading-relaxed text-muted-foreground">Categories sync from <strong className="text-forest">Square</strong>. When Square has no image, the one you add here is shown on the storefront (web-only).</p>
        </div>
      </div>
      {categories.length === 0 ? (
        <div className="rounded-[1.5rem] border border-dashed border-border bg-white p-10 text-center">
          <Layers className="mx-auto h-10 w-10 text-muted-foreground" />
          <p className="mt-4 font-medium text-forest">No categories yet</p>
          <p className="mt-1 text-sm text-muted-foreground">Add categories in Square, then sync to manage their images here.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {categories.map((c) => {
            const [Icon, tint] = categoryIcon(c.name);
            return (
              <div
                key={c.slug}
                data-testid={`admin-category-${c.slug}`}
                className="group relative flex flex-col rounded-[1.5rem] border border-border/70 bg-white p-5 transition-all hover:-translate-y-0.5 hover:border-forest/30 hover:shadow-[0_24px_48px_-28px_rgba(15,74,35,0.45)]"
              >
                <div className="flex items-start justify-between gap-3">
                  <span className={`flex h-14 w-14 items-center justify-center rounded-2xl ${tint}`}>
                    <Icon className="h-7 w-7" strokeWidth={1.75} />
                  </span>
                  <div className="flex items-center gap-1.5">
                    {!c.from_square && (
                      <button
                        data-testid={`delete-category-${c.slug}`}
                        onClick={() => removeCategory(c)}
                        title="Delete category"
                        className="flex h-9 w-9 items-center justify-center rounded-full border border-border text-destructive transition-colors hover:border-destructive/40 hover:bg-destructive/5"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                    <button
                      data-testid={`pin-category-${c.slug}`}
                      onClick={() => togglePin(c)}
                      aria-pressed={!!c.featured}
                      title={c.featured ? "Unpin from homepage" : "Pin to homepage"}
                      className={`flex h-9 w-9 items-center justify-center rounded-full border transition-colors ${c.featured ? "border-clay bg-clay text-white" : "border-border text-forest hover:border-forest"}`}
                    >
                      <Star className={`h-4 w-4 ${c.featured ? "fill-white" : ""}`} />
                    </button>
                  </div>
                </div>
                <p className="mt-5 truncate font-display text-xl font-semibold text-forest">{c.name}</p>
                <div className="mt-1.5 flex flex-wrap items-center gap-2">
                  <span className="text-xs text-muted-foreground">{c.from_square ? "Synced from Square" : "Web only"}</span>
                  {c.featured && (
                    <span className="rounded-full bg-clay/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-clay">Pinned</span>
                  )}
                </div>
                <div className="mt-5 flex items-center justify-between border-t border-border/70 pt-4">
                  <a href={`/category/${c.slug}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground transition-colors hover:text-forest">
                    View page <ArrowUpRight className="h-3.5 w-3.5" />
                  </a>
                  <button
                    data-testid={`edit-category-image-${c.slug}`}
                    onClick={() => setEditing(c)}
                    className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-semibold text-forest transition-colors hover:border-forest hover:bg-forest hover:text-white"
                  >
                    <Pencil className="h-3.5 w-3.5" /> Edit
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <ImageDialog
        open={!!editing}
        title="Category"
        subtitle={editing?.name}
        current={editing?.web_image || ""}
        withDescription
        currentDescription={editing?.description || ""}
        onClose={() => setEditing(null)}
        onSubmit={save}
        onGenerate={genImage}
      />
    </div>
  );
}

function Pagination({ page, pageCount, onPage, total, start, end }) {
  if (pageCount <= 1) return null;
  const btn = "flex h-8 items-center gap-1 rounded-lg border border-border px-3 text-sm font-medium text-forest transition-colors hover:bg-forest/5 disabled:cursor-not-allowed disabled:opacity-40";
  return (
    <div data-testid="admin-pagination" className="mt-5 flex flex-wrap items-center justify-between gap-3">
      <span className="text-xs text-muted-foreground">Showing {start}–{end} of {total}</span>
      <div className="flex items-center gap-2">
        <button data-testid="page-prev" disabled={page <= 1} onClick={() => onPage(page - 1)} className={btn}>
          <ChevronLeft className="h-4 w-4" /> Prev
        </button>
        <span data-testid="page-indicator" className="px-1 text-sm font-semibold text-forest">{page} / {pageCount}</span>
        <button data-testid="page-next" disabled={page >= pageCount} onClick={() => onPage(page + 1)} className={btn}>
          Next <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

function ProductsTab({ products, onSync, syncing, reload }) {
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState(null);
  const PER = 10;
  const q = query.trim().toLowerCase();
  const filtered = q
    ? products.filter((p) => `${p.name} ${p.category}`.toLowerCase().includes(q))
    : products;
  const pageCount = Math.max(1, Math.ceil(filtered.length / PER));
  const current = Math.min(page, pageCount);
  const paged = filtered.slice((current - 1) * PER, current * PER);

  const saveImage = async (image_url) => {
    await api.put(`/admin/products/${editing.id}/image`, { image_url });
    toast.success("Product image updated");
    reload();
  };

  const toggleFavourite = async (p) => {
    try {
      await api.put(`/admin/products/${p.id}/featured`, { featured: !p.featured });
      toast.success(!p.featured ? `${p.name} added to market favourites` : `${p.name} removed from favourites`);
      reload();
    } catch (e) {
      toast.error("Could not update favourite");
    }
  };

  const genImage = async () => {
    const { data } = await api.post(`/admin/products/${editing.id}/generate-image`);
    reload();
    return data.image_url;
  };

  const genMissing = async () => {
    try {
      const { data } = await api.post(`/admin/products/generate-missing-images`);
      toast.success(data.message || "Generating images in the background…");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start bulk generation");
    }
  };

  const removeProduct = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? This can't be undone.`)) return;
    try {
      await api.delete(`/admin/products/${p.id}`);
      toast.success("Product deleted");
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not delete");
    }
  };

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              data-testid="product-search"
              value={query}
              onChange={(e) => { setQuery(e.target.value); setPage(1); }}
              placeholder="Search products…"
              className="w-64 rounded-full border border-border bg-card py-2.5 pl-9 pr-4 text-sm text-forest outline-none focus:border-clay"
            />
          </div>
          <p className="text-sm text-muted-foreground">{q ? `${filtered.length} of ${products.length}` : products.length} products</p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="bulk-generate-btn" onClick={genMissing} className="inline-flex items-center gap-2 rounded-full border border-forest bg-forest px-5 py-2.5 text-sm font-semibold text-sand hover:bg-[#0A3318]">
            <Sparkles className="h-4 w-4" /> Generate missing images
          </button>
          <button data-testid="sync-square-btn" onClick={onSync} disabled={syncing} className="inline-flex items-center gap-2 rounded-full bg-clay px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#A3161F] disabled:opacity-60">
            <RefreshCw className={`h-4 w-4 ${syncing ? "animate-spin" : ""}`} /> {syncing ? "Syncing…" : "Sync from Square"}
          </button>
        </div>
      </div>

      <div className="mb-4 flex items-start gap-2 rounded-xl border border-border bg-sand/40 px-4 py-3" data-testid="catalog-readonly-note">
        <Store className="mt-0.5 h-4 w-4 shrink-0 text-clay" />
        <p className="text-xs text-muted-foreground">Your catalog is managed in <strong className="text-forest">Square</strong> — products, categories, prices and stock sync automatically. Stock also drops as customers buy. Use “Sync from Square” to pull the latest on demand.</p>
      </div>

      <div className="overflow-x-auto rounded-xl border border-border bg-card">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
              <th className="p-4">Product</th>
              <th className="p-4">Category</th>
              <th className="p-4">Price</th>
              <th className="p-4">Stock</th>
              <th className="p-4">Source</th>
              <th className="p-4 text-center">Favourite</th>
              <th className="p-4 text-right">Image</th>
            </tr>
          </thead>
          <tbody>
            {paged.length === 0 && (
              <tr><td colSpan={7} className="p-8 text-center text-sm text-muted-foreground" data-testid="product-no-results">No products match "{query}".</td></tr>
            )}
            {paged.map((p) => (
              <tr key={p.id} data-testid={`admin-product-${p.id}`} className="border-b border-border last:border-0">
                <td className="p-4">
                  <div className="flex items-center gap-3">
                    <img src={resolveImg(p.image_url) || undefined} alt="" data-testid={`product-row-image-${p.id}`} className="h-11 w-11 rounded-lg object-cover" />
                    <span className="font-medium text-forest">{p.name}</span>
                  </div>
                </td>
                <td className="p-4 text-muted-foreground">{p.category}</td>
                <td className="p-4 font-semibold text-forest">{formatCAD(p.price_cents)}</td>
                <td className="p-4">
                  <span className={`font-semibold ${p.stock <= 10 ? "text-clay" : "text-forest"}`} data-testid={`admin-stock-${p.id}`}>{p.stock}</span>
                </td>
                <td className="p-4">
                  <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${p.square_variation_id ? "bg-forest/10 text-forest" : "bg-muted text-muted-foreground"}`}>
                    {p.square_variation_id ? "Square" : "Local"}
                  </span>
                </td>
                <td className="p-4 text-center">
                  <button
                    data-testid={`toggle-favourite-${p.id}`}
                    onClick={() => toggleFavourite(p)}
                    aria-pressed={!!p.featured}
                    title={p.featured ? "Remove from market favourites" : "Add to market favourites"}
                    className={`inline-flex h-8 w-8 items-center justify-center rounded-full border transition-colors ${p.featured ? "border-clay bg-clay text-white" : "border-border text-muted-foreground hover:border-clay hover:text-clay"}`}
                  >
                    <Star className={`h-4 w-4 ${p.featured ? "fill-white" : ""}`} />
                  </button>
                </td>
                <td className="p-4 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      data-testid={`edit-product-image-${p.id}`}
                      onClick={() => setEditing(p)}
                      className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs font-medium text-forest hover:border-clay hover:text-clay"
                    >
                      <ImageIcon className="h-3.5 w-3.5" /> {p.web_image_url ? "Edit" : "Add"}
                    </button>
                    {(!p.square_variation_id || !p.active) && (
                      <button
                        data-testid={`delete-product-${p.id}`}
                        onClick={() => removeProduct(p)}
                        title="Delete product"
                        className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-border text-destructive hover:border-destructive hover:bg-destructive/5"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ImageDialog
        open={!!editing}
        title="Product image"
        subtitle={editing ? `${editing.name}${editing.square_image_url ? " · Square already has an image (shown on storefront)" : ""}` : ""}
        current={editing?.web_image_url || ""}
        onClose={() => setEditing(null)}
        onSubmit={saveImage}
        onGenerate={genImage}
      />

      <Pagination page={current} pageCount={pageCount} onPage={setPage} total={filtered.length}
        start={filtered.length === 0 ? 0 : (current - 1) * PER + 1} end={Math.min(current * PER, filtered.length)} />
    </div>
  );
}

function HighlightPager({ orders, highlight, per, onPage }) {
  useEffect(() => {
    if (!highlight) return;
    const idx = orders.findIndex((o) => o.order_number === highlight);
    if (idx >= 0) onPage(Math.floor(idx / per) + 1);
    // eslint-disable-next-line
  }, [highlight, orders]);
  return null;
}

function OrdersTab({ orders, reload, highlight }) {
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState("");
  const [dateFilter, setDateFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const PER = 10;
  const setStatus = async (id, status) => {
    try {
      await api.put(`/admin/orders/${id}/status`, { status });
      toast.success(`Order marked ${status}`);
      reload();
    } catch (e) {
      toast.error("Could not update status");
    }
  };

  if (orders.length === 0) return <p className="text-muted-foreground">No orders yet.</p>;

  const q = query.trim().toLowerCase();
  const inRange = (iso) => {
    if (dateFilter === "all") return true;
    const d = new Date(iso);
    if (isNaN(d)) return false;
    const now = new Date();
    const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    if (dateFilter === "today") return d >= startToday;
    if (dateFilter === "yesterday") {
      const y = new Date(startToday); y.setDate(y.getDate() - 1);
      return d >= y && d < startToday;
    }
    if (dateFilter === "week") {
      const w = new Date(startToday); w.setDate(w.getDate() - w.getDay());
      return d >= w;
    }
    if (dateFilter === "month") return d >= new Date(now.getFullYear(), now.getMonth(), 1);
    if (dateFilter === "30days") {
      const t = new Date(startToday); t.setDate(t.getDate() - 30);
      return d >= t;
    }
    return true;
  };
  const filtered = orders.filter((o) => {
    const matchesQuery = !q || `${o.order_number} ${o.customer_name} ${o.customer_email}`.toLowerCase().includes(q);
    const matchesStatus = statusFilter === "all" || o.status === statusFilter;
    return matchesQuery && matchesStatus && inRange(o.created_at);
  });

  const pageCount = Math.max(1, Math.ceil(filtered.length / PER));
  const current = Math.min(page, pageCount);
  const paged = filtered.slice((current - 1) * PER, current * PER);
  const selectCls = "rounded-full border border-border bg-card px-4 py-2.5 text-sm text-forest outline-none focus:border-clay";

  return (
    <div className="space-y-4">
      <HighlightPager orders={orders} highlight={highlight} per={PER} onPage={setPage} />

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            data-testid="order-search"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setPage(1); }}
            placeholder="Search order #, name or email…"
            className="w-72 rounded-full border border-border bg-card py-2.5 pl-9 pr-4 text-sm text-forest outline-none focus:border-clay"
          />
        </div>
        <div className="relative flex items-center gap-2">
          <CalendarDays className="h-4 w-4 text-muted-foreground" />
          <select
            data-testid="order-date-filter"
            value={dateFilter}
            onChange={(e) => { setDateFilter(e.target.value); setPage(1); }}
            className={selectCls}
          >
            <option value="all">All time</option>
            <option value="today">Today</option>
            <option value="yesterday">Yesterday</option>
            <option value="week">This week</option>
            <option value="month">This month</option>
            <option value="30days">Last 30 days</option>
          </select>
        </div>
        <select
          data-testid="order-status-filter"
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          className={selectCls}
        >
          <option value="all">All statuses</option>
          {ALL_STATUSES.map((s) => (
            <option key={s} value={s}>{statusLabel(s).charAt(0).toUpperCase() + statusLabel(s).slice(1)}</option>
          ))}
        </select>
        <p className="text-sm text-muted-foreground" data-testid="order-result-count">{filtered.length} of {orders.length} orders</p>
      </div>

      {filtered.length === 0 && (
        <p data-testid="order-no-results" className="rounded-xl border border-border bg-card p-8 text-center text-sm text-muted-foreground">No orders match your search or date filter.</p>
      )}

      {paged.map((o) => (
        <div key={o.id} data-testid={`admin-order-${o.order_number}`} className={`rounded-xl border bg-card p-5 transition-shadow ${highlight === o.order_number ? "border-clay ring-2 ring-clay/30" : "border-border"}`}>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-3">
                <p className="font-semibold text-forest">{o.order_number}</p>
                <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize ${STATUS_COLORS[o.status]}`}>{statusLabel(o.status)}</span>
                {o.payment?.mocked && <span className="rounded-full bg-ochre/15 px-2.5 py-0.5 text-xs text-forest">demo pay</span>}
              </div>
              <p className="mt-1 text-sm text-muted-foreground">{o.customer_name} · {o.customer_email}</p>
              <p className="text-xs text-muted-foreground">{new Date(o.created_at).toLocaleString("en-CA")}</p>
            </div>
            <div className="text-right">
              <p className="text-lg font-bold text-forest">{formatCAD(o.total_cents)}</p>
              <p className="text-xs text-muted-foreground">{o.items.reduce((s, i) => s + i.quantity, 0)} items · {o.shipping_method?.name}</p>
              {o.shipping_method?.id === "pickup" && o.pickup_slot && (
                <p className="text-xs font-medium text-clay">Pickup: {o.pickup_slot}</p>
              )}
            </div>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-border pt-4">
            <span className="text-xs font-medium text-muted-foreground">Update status:</span>
            {statusesFor(o).map((s) => (
              <button
                key={s}
                data-testid={`order-status-${o.order_number}-${s}`}
                onClick={() => setStatus(o.id, s)}
                className={`rounded-full px-3 py-1 text-xs font-medium capitalize transition-colors ${o.status === s ? "bg-clay text-white" : "border border-border text-forest hover:bg-forest/5"}`}
              >
                {statusLabel(s)}
              </button>
            ))}
          </div>
        </div>
      ))}

      <Pagination page={current} pageCount={pageCount} onPage={setPage} total={filtered.length}
        start={filtered.length === 0 ? 0 : (current - 1) * PER + 1} end={Math.min(current * PER, filtered.length)} />
    </div>
  );
}

function DiscountsTab({ discounts, reload }) {
  const empty = { code: "", type: "percent", value: 10, min_subtotal_cents: 0, usage_limit: 0, active: true };
  const [form, setForm] = useState(empty);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const openNew = () => { setEditing(null); setForm(empty); setShowForm(true); };
  const openEdit = (d) => {
    setEditing(d.id);
    setForm({ code: d.code, type: d.type, value: d.value, min_subtotal_cents: d.min_subtotal_cents, usage_limit: d.usage_limit, active: d.active });
    setShowForm(true);
  };

  const save = async () => {
    try {
      const body = { ...form, value: Number(form.value), min_subtotal_cents: Number(form.min_subtotal_cents), usage_limit: Number(form.usage_limit) };
      if (editing) await api.put(`/admin/discounts/${editing}`, body);
      else await api.post("/admin/discounts", body);
      toast.success("Discount saved");
      setShowForm(false);
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this code?")) return;
    await api.delete(`/admin/discounts/${id}`);
    toast.success("Deleted");
    reload();
  };

  const fld = "w-full rounded-lg border border-border bg-card px-3 py-2 text-sm outline-none focus:border-clay";

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{discounts.length} code{discounts.length !== 1 ? "s" : ""}</p>
        <button data-testid="add-discount-btn" onClick={openNew} className="inline-flex items-center gap-2 rounded-full bg-clay px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#A3161F]">
          <Plus className="h-4 w-4" /> New code
        </button>
      </div>

      {discounts.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-card p-10 text-center">
          <Tag className="mx-auto h-10 w-10 text-muted-foreground" />
          <p className="mt-4 font-medium text-forest">No discount codes yet</p>
          <p className="mt-1 text-sm text-muted-foreground">Create a promo code to run a sale or win-back offer.</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border bg-card">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                <th className="p-4">Code</th><th className="p-4">Discount</th><th className="p-4">Min spend</th><th className="p-4">Used</th><th className="p-4">Status</th><th className="p-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {discounts.map((d) => (
                <tr key={d.id} data-testid={`discount-${d.code}`} className="border-b border-border last:border-0">
                  <td className="p-4 font-mono font-semibold text-forest">{d.code}</td>
                  <td className="p-4 text-forest">{d.type === "percent" ? `${d.value}%` : formatCAD(d.value)}</td>
                  <td className="p-4 text-muted-foreground">{d.min_subtotal_cents ? formatCAD(d.min_subtotal_cents) : "—"}</td>
                  <td className="p-4 text-muted-foreground">{d.used_count}{d.usage_limit ? ` / ${d.usage_limit}` : ""}</td>
                  <td className="p-4"><span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${d.active ? "bg-forest/10 text-forest" : "bg-muted text-muted-foreground"}`}>{d.active ? "active" : "inactive"}</span></td>
                  <td className="p-4">
                    <div className="flex justify-end gap-2">
                      <button data-testid={`edit-discount-${d.code}`} onClick={() => openEdit(d)} className="flex h-8 w-8 items-center justify-center rounded-md border border-border text-forest hover:bg-forest/5"><Pencil className="h-4 w-4" /></button>
                      <button data-testid={`delete-discount-${d.code}`} onClick={() => remove(d.id)} className="flex h-8 w-8 items-center justify-center rounded-md border border-border text-destructive hover:bg-destructive/5"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-forest/40 p-4" onClick={() => setShowForm(false)}>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-2xl bg-card p-6 shadow-xl" data-testid="discount-form-modal">
            <div className="flex items-center justify-between">
              <h3 className="font-display text-2xl font-medium text-forest">{editing ? "Edit code" : "New code"}</h3>
              <button onClick={() => setShowForm(false)}><X className="h-5 w-5 text-muted-foreground" /></button>
            </div>
            <div className="mt-5 grid gap-3">
              <input data-testid="df-code" className={`${fld} uppercase`} placeholder="Code (e.g. WELCOME10)" value={form.code} disabled={!!editing} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} />
              <div className="grid grid-cols-2 gap-3">
                <select data-testid="df-type" className={fld} value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                  <option value="percent">Percent %</option>
                  <option value="fixed">Fixed $ off</option>
                </select>
                <div>
                  <input data-testid="df-value" type="number" className={fld} placeholder={form.type === "percent" ? "% off" : "cents off"} value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} />
                  <p className="mt-1 text-[11px] text-muted-foreground">{form.type === "percent" ? "1–100 (%)" : "amount in cents (e.g. 500 = $5)"}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-muted-foreground">Min spend (cents)</label>
                  <input className={fld} type="number" value={form.min_subtotal_cents} onChange={(e) => setForm({ ...form, min_subtotal_cents: e.target.value })} />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground">Usage limit (0 = ∞)</label>
                  <input className={fld} type="number" value={form.usage_limit} onChange={(e) => setForm({ ...form, usage_limit: e.target.value })} />
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm text-forest">
                <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="accent-[#C8202B]" /> Active
              </label>
            </div>
            <button data-testid="df-save" onClick={save} className="mt-5 w-full rounded-full bg-clay py-3 text-sm font-semibold text-white hover:bg-[#A3161F]">Save code</button>
          </motion.div>
        </div>
      )}
    </div>
  );
}

function CartsTab({ carts, reload }) {
  const [sending, setSending] = useState(false);
  const open = carts.filter((c) => c.status === "active" && !c.nudged);
  const reminded = carts.filter((c) => c.status === "active" && c.nudged);

  const nudge = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/admin/carts/nudge");
      toast.success(data.message);
      reload();
    } catch (e) {
      toast.error("Could not send reminders");
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">{open.length} open · {reminded.length} reminded · {carts.length} total</p>
        <button
          data-testid="nudge-carts-btn"
          onClick={nudge}
          disabled={sending}
          className="inline-flex items-center gap-2 rounded-full bg-clay px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#A3161F] disabled:opacity-60"
        >
          <Send className={`h-4 w-4 ${sending ? "animate-pulse" : ""}`} /> Send reminders now
        </button>
      </div>

      {carts.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-card p-10 text-center">
          <Mail className="mx-auto h-10 w-10 text-muted-foreground" />
          <p className="mt-4 font-medium text-forest">No abandoned carts yet</p>
          <p className="mt-1 text-sm text-muted-foreground">Carts appear here when a shopper enters their email at checkout but doesn't complete the order.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {carts.map((c) => (
            <div key={c.email} data-testid={`cart-row-${c.email}`} className="rounded-xl border border-border bg-card p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-forest">{c.name || "Guest"}</p>
                    <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize ${c.status === "converted" ? "bg-forest/10 text-forest" : c.nudged ? "bg-ochre/20 text-forest" : "bg-clay/15 text-clay"}`}>
                      {c.status === "converted" ? "purchased" : c.nudged ? "reminded" : "active"}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground">{c.email}</p>
                  <p className="text-xs text-muted-foreground">Updated {new Date(c.updated_at).toLocaleString("en-CA")}</p>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-forest">{formatCAD(c.subtotal_cents)}</p>
                  <p className="text-xs text-muted-foreground">{c.items.reduce((s, i) => s + i.quantity, 0)} items</p>
                </div>
              </div>
              <div className="mt-3 flex -space-x-3 border-t border-border pt-3">
                {c.items.slice(0, 6).map((it) => (
                  <img key={it.product_id} src={it.image_url || undefined} alt="" className="h-9 w-9 rounded-full border-2 border-card object-cover" />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SettingsTab() {
  const [gst, setGst] = useState("");
  const [delivery, setDelivery] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/admin/settings")
      .then((r) => { setGst(r.data.gst_number || ""); setDelivery(r.data.delivery); setLoaded(true); })
      .catch(() => setLoaded(true));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", { gst_number: gst.trim(), delivery });
      toast.success("Store settings saved");
    } catch (e) {
      toast.error("Could not save settings");
    } finally {
      setSaving(false);
    }
  };

  const setD = (k, v) => setDelivery((d) => ({ ...d, [k]: v }));
  const zones = delivery?.zones || [];
  const setZones = (z) => setDelivery((d) => ({ ...d, zones: z }));
  const setZone = (i, k, v) => setZones(zones.map((z, idx) => (idx === i ? { ...z, [k]: v } : z)));
  const addZone = () => setZones([...zones, { label: `Zone ${zones.length + 1}`, max_km: 5, surcharge_cents: 0 }]);
  const removeZone = (i) => setZones(zones.filter((_, idx) => idx !== i));
  const zoneMaxKm = Math.max(delivery?.max_radius_km || 1, ...zones.map((z) => z.max_km || 0), 1);
  // money fields are stored in cents; edited in dollars
  const dollars = (cents) => (cents == null ? "" : (cents / 100).toFixed(2));
  const toCents = (val) => Math.max(0, Math.round(parseFloat(val || "0") * 100));

  const Money = ({ k, label, hint }) => (
    <div>
      <label className="text-xs font-medium text-forest">{label}</label>
      <div className="relative mt-1">
        <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">$</span>
        <input data-testid={`delivery-${k}`} type="number" step="0.01" min="0" className={`${field} pl-7`}
          value={dollars(delivery?.[k])} onChange={(e) => setD(k, toCents(e.target.value))} />
      </div>
      {hint && <p className="mt-1 text-[11px] text-muted-foreground">{hint}</p>}
    </div>
  );
  const Num = ({ k, label, step = "1", hint }) => (
    <div>
      <label className="text-xs font-medium text-forest">{label}</label>
      <input data-testid={`delivery-${k}`} type="number" step={step} min="0" className={`${field} mt-1`}
        value={delivery?.[k] ?? ""} onChange={(e) => setD(k, step === "1" ? parseInt(e.target.value || "0", 10) : parseFloat(e.target.value || "0"))} />
      {hint && <p className="mt-1 text-[11px] text-muted-foreground">{hint}</p>}
    </div>
  );
  const Time = ({ k, label }) => (
    <div>
      <label className="text-xs font-medium text-forest">{label}</label>
      <input data-testid={`delivery-${k}`} type="time" className={`${field} mt-1`}
        value={delivery?.[k] ?? ""} onChange={(e) => setD(k, e.target.value)} />
    </div>
  );

  return (
    <div className="max-w-3xl space-y-6">
      <div className="rounded-xl border border-border bg-card p-6">
        <h2 className="font-display text-2xl font-medium text-forest">Store settings</h2>
        <p className="mt-1 text-sm text-muted-foreground">These details appear on customer receipts and order confirmation emails.</p>
        <div className="mt-6 max-w-md">
          <label className="text-sm font-medium text-forest">GST / HST Number</label>
          <input
            data-testid="settings-gst-input"
            className={`${field} mt-2`}
            placeholder="e.g. 123456789 RT0001"
            value={gst}
            onChange={(e) => setGst(e.target.value)}
            disabled={!loaded}
          />
          <p className="mt-2 text-xs text-muted-foreground">Leave blank to hide the GST line from receipts.</p>
        </div>
      </div>

      {delivery && (
        <div className="rounded-xl border border-border bg-card p-6" data-testid="delivery-settings-panel">
          <h2 className="font-display text-2xl font-medium text-forest">Delivery pricing</h2>
          <p className="mt-1 text-sm text-muted-foreground">In-house courier fees are calculated from driving distance, drive time (with live traffic), and fuel. Adjust the factors below.</p>

          <h3 className="mt-6 text-xs font-semibold uppercase tracking-wide text-clay">Base charges</h3>
          <div className="mt-3 grid gap-4 sm:grid-cols-3">
            <Money k="base_fee_cents" label="Base fee" />
            <Money k="per_km_cents" label="Per km" />
            <Money k="per_min_cents" label="Per minute" />
            <Money k="min_fee_cents" label="Minimum fee" />
            <Num k="max_radius_km" label="Max delivery radius (km)" hint="Beyond this, only pickup is offered" />
            <Money k="free_delivery_threshold_cents" label="Free delivery over" hint="Set 0 to disable" />
          </div>

          <h3 className="mt-6 text-xs font-semibold uppercase tracking-wide text-clay">Fuel surcharge</h3>
          <div className="mt-3 grid gap-4 sm:grid-cols-2">
            <Num k="fuel_price_per_l" label="Fuel price ($/L)" step="0.01" />
            <Num k="fuel_consumption_l_per_km" label="Fuel use (L/km)" step="0.01" />
          </div>

          <h3 className="mt-6 text-xs font-semibold uppercase tracking-wide text-clay">Rush hour</h3>
          <div className="mt-3 grid gap-4 sm:grid-cols-3">
            <Num k="rush_multiplier" label="Rush multiplier (×)" step="0.05" />
            <Time k="rush_morning_start" label="Morning start" />
            <Time k="rush_morning_end" label="Morning end" />
            <Time k="rush_evening_start" label="Evening start" />
            <Time k="rush_evening_end" label="Evening end" />
            <label className="flex items-center gap-2 self-end pb-2 text-sm text-forest">
              <input data-testid="delivery-rush_weekends" type="checkbox" checked={!!delivery.rush_weekends}
                onChange={(e) => setD("rush_weekends", e.target.checked)} className="accent-[#C8202B]" />
              Apply on weekends
            </label>
          </div>

          <h3 className="mt-6 text-xs font-semibold uppercase tracking-wide text-clay">Express delivery surcharge</h3>
          <div className="mt-3 grid gap-4 sm:grid-cols-3">
            <div>
              <label className="text-xs font-medium text-forest">Surcharge type</label>
              <select data-testid="delivery-express_mode" className={`${field} mt-1`}
                value={delivery.express_mode} onChange={(e) => setD("express_mode", e.target.value)}>
                <option value="flat">Flat amount</option>
                <option value="percent">Percentage of standard fee</option>
              </select>
            </div>
            {delivery.express_mode === "percent"
              ? <Num k="express_percent" label="Express extra (%)" step="1" />
              : <Money k="express_flat_cents" label="Express extra ($)" />}
          </div>

          <h3 className="mt-6 text-xs font-semibold uppercase tracking-wide text-clay">Delivery zones</h3>
          <p className="mt-1 text-xs text-muted-foreground">Distance bands from your store; each adds a surcharge on top of the calculated fee. Drag the orange circle edge on the map to adjust your max radius ({delivery.max_radius_km} km).</p>
          <div className="mt-4 grid gap-6 md:grid-cols-2">
            {GMAP_KEY ? (
              <DeliveryZoneMap
                maxRadiusKm={delivery.max_radius_km}
                zones={zones}
                onRadius={(km) => setD("max_radius_km", km)}
              />
            ) : (
            <div className="flex flex-col items-center justify-center rounded-xl border border-border bg-sand/40 p-4" data-testid="delivery-zone-map">
              <svg viewBox="0 0 220 220" className="h-52 w-52">
                {[...zones].sort((a, b) => (b.max_km || 0) - (a.max_km || 0)).map((z, idx) => {
                  const r = Math.max(10, ((z.max_km || 0) / zoneMaxKm) * 100);
                  return (
                    <g key={idx}>
                      <circle cx="110" cy="110" r={r} fill={`rgba(15,74,35,${0.08 + idx * 0.08})`} stroke="#0F4A23" strokeOpacity="0.3" />
                      <text x="110" y={110 - r + 12} textAnchor="middle" fontSize="9" fill="#0F4A23" fontWeight="600">{z.max_km}km</text>
                    </g>
                  );
                })}
                <circle cx="110" cy="110" r="5" fill="#C8202B" />
                <text x="110" y="128" textAnchor="middle" fontSize="9" fill="#C8202B" fontWeight="700">Store</text>
              </svg>
              <p className="mt-2 text-[11px] text-muted-foreground">Concentric delivery zones (to scale by distance)</p>
            </div>
            )}
            <div className="space-y-3">
              {zones.map((z, i) => (
                <div key={i} data-testid={`zone-row-${i}`} className="rounded-xl border border-border p-3">
                  <div className="grid grid-cols-2 gap-2">
                    <input data-testid={`zone-label-${i}`} className={field} placeholder="Zone name" value={z.label}
                      onChange={(e) => setZone(i, "label", e.target.value)} />
                    <div className="flex items-center gap-1">
                      <input data-testid={`zone-maxkm-${i}`} type="number" min="0" step="0.5" className={field} value={z.max_km}
                        onChange={(e) => setZone(i, "max_km", parseFloat(e.target.value || "0"))} />
                      <span className="text-xs text-muted-foreground">km</span>
                    </div>
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    <div className="relative flex-1">
                      <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">$</span>
                      <input data-testid={`zone-surcharge-${i}`} type="number" min="0" step="0.01" className={`${field} pl-7`}
                        value={(z.surcharge_cents / 100).toFixed(2)}
                        onChange={(e) => setZone(i, "surcharge_cents", Math.max(0, Math.round(parseFloat(e.target.value || "0") * 100)))} />
                    </div>
                    <span className="text-xs text-muted-foreground">surcharge</span>
                    <button data-testid={`zone-remove-${i}`} onClick={() => removeZone(i)} className="rounded-lg border border-border p-2 text-destructive hover:bg-destructive/5">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
              <button data-testid="add-zone" onClick={addZone} className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm font-medium text-forest hover:bg-forest/5">
                <Plus className="h-4 w-4" /> Add zone
              </button>
            </div>
          </div>
        </div>
      )}

      <button data-testid="settings-save" onClick={save} disabled={saving || !loaded} className="rounded-full bg-clay px-6 py-2.5 text-sm font-semibold text-white hover:bg-[#A3161F] disabled:opacity-60">
        {saving ? "Saving…" : "Save settings"}
      </button>
    </div>
  );
}

const greeting = () => {
  const h = new Date().getHours();
  return h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening";
};

export default function Admin() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get("tab") || "overview");
  // Keep tab in sync with URL changes (e.g. notification click while already on /admin)
  useEffect(() => {
    const t = searchParams.get("tab");
    if (t && t !== tab) setTab(t);
    // eslint-disable-next-line
  }, [searchParams]);
  const [stats, setStats] = useState(null);
  const [square, setSquare] = useState(null);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [orders, setOrders] = useState([]);
  const [carts, setCarts] = useState([]);
  const [discounts, setDiscounts] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [syncing, setSyncing] = useState(false);

  const loadAll = () => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/products").then((r) => setProducts(r.data)).catch(() => {});
    api.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/admin/carts").then((r) => setCarts(r.data)).catch(() => {});
    api.get("/admin/discounts").then((r) => setDiscounts(r.data)).catch(() => {});
    api.get("/admin/restock-requests").then((r) => setWaitlist(r.data)).catch(() => {});
    api.get("/square/status").then((r) => setSquare(r.data)).catch(() => {});
    api.get("/categories", { params: { include_web: true } }).then((r) => setCategories(r.data)).catch(() => {});
  };

  useEffect(() => {
    if (user && user.role === "admin") loadAll();
  }, [user]);

  // Reload orders when navigating with an ?order= param (e.g. from notification click)
  // so the freshly-placed order appears in the OrdersTab for highlighting.
  useEffect(() => {
    if (user && user.role === "admin" && searchParams.get("order")) {
      api.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => {});
    }
    // eslint-disable-next-line
  }, [searchParams]);

  const sync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/square/sync");
      toast[data.synced ? "success" : "info"](data.message);
      loadAll();
    } catch (e) {
      toast.error("Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  if (user === null) return <div className="py-32 text-center text-muted-foreground">Loading…</div>;
  if (!user || user.role !== "admin")
    return (
      <div className="mx-auto max-w-md px-5 py-32 text-center">
        <h1 className="font-display text-4xl font-semibold text-forest">Admin access only</h1>
        <p className="mt-2 text-muted-foreground">Please sign in with an admin account.</p>
        <Link to="/account" className="mt-6 inline-block rounded-full bg-clay px-6 py-3 text-sm font-semibold text-white hover:bg-[#A3161F]">Go to sign in</Link>
      </div>
    );

  const tabs = [
    { id: "overview", label: "Overview", icon: LayoutDashboard },
    { id: "products", label: "Products", icon: Package },
    { id: "categories", label: "Categories", icon: Layers },
    { id: "waitlist", label: "Waitlist", icon: Bell, badge: waitlist.length },
    { id: "orders", label: "Orders", icon: ShoppingCart, badge: stats?.status_counts?.pending || 0 },
    { id: "discounts", label: "Discounts", icon: Tag },
    { id: "carts", label: "Abandoned Carts", icon: Mail },
    { id: "settings", label: "Settings", icon: Settings },
  ];

  return (
    <div data-testid="admin-page" className="mx-auto max-w-[1440px] px-3 pb-10 pt-3 sm:px-5 lg:px-8">
      {/* Header banner */}
      <div className="relative overflow-hidden rounded-[2rem] bg-forest px-6 py-8 text-white sm:px-10 lg:py-10">
        <div className="pattern-dots absolute inset-0" />
        <img src={ASSETS.logoMark} alt="" aria-hidden="true" className="pointer-events-none absolute -bottom-12 right-6 hidden h-60 w-auto opacity-[0.12] md:block" />
        <div className="relative flex flex-wrap items-end justify-between gap-6">
          <div>
            <div className="flex flex-wrap items-center gap-3">
              <span className="label-eyebrow text-ochre">Admin dashboard</span>
              <StoreStatusBadge className="!bg-white/10 !text-white" />
            </div>
            <h1 className="mt-3 font-display text-4xl font-semibold tracking-tight text-white lg:text-5xl">
              {greeting()}, {(user.name || "there").split(" ")[0]}
            </h1>
            <p className="mt-2 text-sm text-sand/75">
              {new Date().toLocaleDateString("en-CA", { weekday: "long", month: "long", day: "numeric" })} · Manage products, orders & inventory
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link to="/" target="_blank" className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/5 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-white/15">
              <ExternalLink className="h-4 w-4" /> View store
            </Link>
            <button
              onClick={sync}
              disabled={syncing}
              className="inline-flex items-center gap-2 rounded-full bg-ochre px-4 py-2.5 text-sm font-bold text-ink transition-colors hover:bg-white disabled:opacity-60"
            >
              <RefreshCw className={`h-4 w-4 ${syncing ? "animate-spin" : ""}`} /> Sync Square
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="sticky top-[104px] z-30 -mx-3 mt-4 bg-sand/95 px-3 py-2 backdrop-blur sm:mx-0 sm:px-0">
        <div className="no-scrollbar flex gap-1 overflow-x-auto rounded-full border border-border/70 bg-white p-1.5">
          {tabs.map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`inline-flex shrink-0 items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${tab === t.id ? "bg-forest text-white shadow-sm" : "text-ink/65 hover:bg-sand hover:text-forest"}`}
            >
              <t.icon className="h-4 w-4" /> {t.label}
              {t.badge > 0 && (
                <span className={`rounded-full px-1.5 text-[10px] font-bold ${tab === t.id ? "bg-white/20 text-white" : "bg-clay text-white"}`}>{t.badge}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-6">
        {tab === "overview" && (
          <Overview
            stats={stats}
            square={square}
            onSync={sync}
            syncing={syncing}
            orders={orders}
            products={products}
            waitlist={waitlist}
            carts={carts}
            goTo={setTab}
          />
        )}
        {tab === "products" && <ProductsTab products={products} onSync={sync} syncing={syncing} reload={loadAll} />}
        {tab === "categories" && <CategoriesTab categories={categories} reload={loadAll} />}
        {tab === "waitlist" && <WaitlistTab waitlist={waitlist} />}
        {tab === "orders" && <OrdersTab orders={orders} reload={loadAll} highlight={searchParams.get("order")} />}
        {tab === "discounts" && <DiscountsTab discounts={discounts} reload={loadAll} />}
        {tab === "carts" && <CartsTab carts={carts} reload={loadAll} />}
        {tab === "settings" && <SettingsTab />}
      </div>
    </div>
  );
}
